import { Component, Input, Output, EventEmitter, SimpleChanges, OnChanges, inject } from '@angular/core';
import { TableColumn, TableAction } from '../models/table-models';
import { CommonModule, formatDate, formatNumber } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/**
 * Page buttons for a pager: first, last and the pages around the current one,
 * with `null` where a run of pages is collapsed into "…" (e.g. 1 2 3 … 147).
 */
export function pageWindow(current: number, totalPages: number): (number | null)[] {
  const total = Math.max(1, totalPages);
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const shown = new Set([1, total, current - 1, current, current + 1]);
  if (current <= 3) [2, 3].forEach((p) => shown.add(p));
  if (current >= total - 2) [total - 2, total - 1].forEach((p) => shown.add(p));

  const pages = [...shown].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: (number | null)[] = [];

  pages.forEach((page, i) => {
    const gap = i > 0 ? page - pages[i - 1] : 1;
    if (gap === 2) out.push(page - 1); // a single hidden page is shown instead of "…"
    else if (gap > 2) out.push(null);
    out.push(page);
  });

  return out;
}

@Component({
  selector: 'app-table',
  imports: [CommonModule, RouterModule, FontAwesomeModule, FormsModule, TranslatePipe],
  templateUrl: './table.html',
  styleUrl: './table.css',
})
export class Table implements OnChanges {
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() actions: TableAction[] = [];
  @Input() selectable = false;
  @Output() selectionChange = new EventEmitter<any[]>();
  faMagnifyingGlass = faMagnifyingGlass;
  @Input() pageSize: number = 10;
  @Input() enablePagination: boolean = false;

  // Search
  @Input() enableSearch: boolean = true;
  @Input() searchPlaceholder: string = 'COMMON.SEARCH_PLACEHOLDER';

  searchTerm = '';
  currentPage: number = 1;

  private translate = inject(TranslateService);

  selectedRows: Set<any> = new Set();
  isAllSelected = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['columns']) {
      this.currentPage = 1;
      this.checkAllSelected();
    }
  }

  onSearchChange(): void {
    this.currentPage = 1;
    this.checkAllSelected();
  }

  private normalizeSearchValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (value instanceof Date) {
      return value.toLocaleDateString().toLowerCase();
    }

    if (typeof value === 'object') {
      return Object.values(value)
        .map((v) => this.normalizeSearchValue(v))
        .join(' ')
        .toLowerCase();
    }

    return String(value).toLowerCase();
  }

  private getColumnSearchValue(row: any, col: TableColumn): string {
    const rawValue = row?.[col.key];

    const mappedValue =
      col.map && rawValue !== null && rawValue !== undefined && col.map[rawValue]
        ? this.translate.instant(col.map[rawValue])
        : typeof rawValue === 'string' && rawValue
          ? this.translate.instant(rawValue) // plain text cells are shown through the translate pipe too
          : '';

    // Also match what the cell actually renders, so searching "04-Aug-2026"
    // or "1,250,000" hits the same row as the raw ISO date / plain number.
    const displayValue = this.getDisplaySearchValue(rawValue, col);

    return `${this.normalizeSearchValue(rawValue)} ${this.normalizeSearchValue(mappedValue)} ${this.normalizeSearchValue(displayValue)}`;
  }

  /** Amount columns read right-aligned unless the column says otherwise. */
  isRightAligned(col: TableColumn): boolean {
    return col.align ? col.align === 'right' : col.type === 'currency';
  }

  private getDisplaySearchValue(value: any, col: TableColumn): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    try {
      if (col.type === 'date') {
        return formatDate(value, 'dd-MMM-yyyy', 'en-US');
      }

      if (col.type === 'currency') {
        return Number(value) > 0 ? formatNumber(Number(value), 'en-US', '1.0-0') : '';
      }
    } catch {
      return '';
    }

    return '';
  }

  get filteredData(): any[] {
    const rows = Array.isArray(this.data) ? this.data : [];
    const term = this.searchTerm.trim().toLowerCase();

    if (!term) {
      return rows;
    }

    const searchWords = term.split(/\s+/).filter(Boolean);

    return rows.filter((row) => {
      const columnText = this.columns.map((col) => this.getColumnSearchValue(row, col)).join(' ');

      const fullRowText = this.normalizeSearchValue(row);

      const searchableText = `${columnText} ${fullRowText}`.toLowerCase();

      return searchWords.every((word) => searchableText.includes(word));
    });
  }

  toggleAll(event: any): void {
    this.isAllSelected = event.target.checked;

    const rowsToSelect = this.filteredData;

    if (this.isAllSelected) {
      rowsToSelect.forEach((row) => this.selectedRows.add(row));
    } else {
      rowsToSelect.forEach((row) => this.selectedRows.delete(row));
    }

    this.emitSelection();
  }

  toggleRow(row: any, event: any): void {
    if (event.target.checked) {
      this.selectedRows.add(row);
    } else {
      this.selectedRows.delete(row);
    }

    this.checkAllSelected();
    this.emitSelection();
  }

  checkAllSelected(): void {
    const rows = this.filteredData;

    this.isAllSelected = rows.length > 0 && rows.every((row) => this.selectedRows.has(row));
  }

  clearSelection(): void {
    this.selectedRows.clear();
    this.isAllSelected = false;
    this.emitSelection();
  }

  isSelected(row: any): boolean {
    return this.selectedRows.has(row);
  }

  emitSelection(): void {
    this.selectionChange.emit(Array.from(this.selectedRows));
  }

  isImagePath(icon: any): boolean {
    return typeof icon === 'string';
  }

  shouldHide(action: TableAction, row: any): boolean {
    const isVisible = action.visible ? action.visible(row) : true;
    const isHidden = action.hide ? action.hide(row) : false;

    return !isVisible || isHidden;
  }

  get totalRecords(): number {
    return this.filteredData.length;
  }

  get startRecord(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get endRecord(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  get paginatedData(): any[] {
    const rows = this.filteredData;

    if (!this.enablePagination) {
      return rows;
    }

    const start = (this.currentPage - 1) * this.pageSize;

    return rows.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
  }

  get pages(): (number | null)[] {
    return pageWindow(this.currentPage, this.totalPages);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  nextPage(): void {
    if (this.endRecord < this.totalRecords) {
      this.currentPage++;
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }
}
